import Keycloak from 'keycloak-js';

import { config } from '@/config';

const LOCALSTORAGE_KEYS = {
  TOKEN: 'kc_token',
  REFRESH_TOKEN: 'kc_refresh_token',
  ID_TOKEN: 'kc_id_token',
} as const;

const SSO_COOKIE_KEYS = {
  TOKEN: `kc_sso_token_${config.ENVIRONMENT}`,
  REFRESH_TOKEN: `kc_sso_refresh_${config.ENVIRONMENT}`,
  ID_TOKEN: `kc_sso_id_${config.ENVIRONMENT}`,
};

export function createKeycloakInstance(): Keycloak {
  return new Keycloak({
    url: config.KEYCLOAK_URL,
    realm: config.KEYCLOAK_REALM,
    clientId: config.KEYCLOAK_CLIENT_ID,
  });
}

function setCookie(name: string, value: string): void {
  const domain = config.SSO_COOKIE_DOMAIN || '';
  const domainPart = domain ? `; domain=${domain}` : '';
  const securePart = location.protocol === 'https:' ? '; secure' : '';
  const maxAge = 60 * 60 * 24 * 30; // 30 days
  document.cookie = `${name}=${value}; path=/${domainPart}${securePart}; max-age=${maxAge}; samesite=lax`;
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : undefined;
}

function deleteCookie(name: string): void {
  const domain = config.SSO_COOKIE_DOMAIN || '';
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; path=/${domainPart}; max-age=0`;
}

export function persistTokens(kc: Keycloak): void {
  if (kc.token) localStorage.setItem(LOCALSTORAGE_KEYS.TOKEN, kc.token);
  if (kc.refreshToken) localStorage.setItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN, kc.refreshToken);
  if (kc.idToken) localStorage.setItem(LOCALSTORAGE_KEYS.ID_TOKEN, kc.idToken);
  if (kc.token && kc.refreshToken && kc.idToken) {
    setCookie(SSO_COOKIE_KEYS.TOKEN, kc.token);
    setCookie(SSO_COOKIE_KEYS.REFRESH_TOKEN, kc.refreshToken);
    setCookie(SSO_COOKIE_KEYS.ID_TOKEN, kc.idToken);
  }
}

export function persistTokensRaw(token: string, refreshToken: string, idToken: string): void {
  localStorage.setItem(LOCALSTORAGE_KEYS.TOKEN, token);
  localStorage.setItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(LOCALSTORAGE_KEYS.ID_TOKEN, idToken);
  setCookie(SSO_COOKIE_KEYS.TOKEN, token);
  setCookie(SSO_COOKIE_KEYS.REFRESH_TOKEN, refreshToken);
  setCookie(SSO_COOKIE_KEYS.ID_TOKEN, idToken);
}

export function loadPersistedTokens(): {
  token?: string;
  refreshToken?: string;
  idToken?: string;
} {
  const token = localStorage.getItem(LOCALSTORAGE_KEYS.TOKEN) ?? undefined;
  const refreshToken = localStorage.getItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN) ?? undefined;
  const idToken = localStorage.getItem(LOCALSTORAGE_KEYS.ID_TOKEN) ?? undefined;

  if (token && refreshToken) {
    const cookieToken = getCookie(SSO_COOKIE_KEYS.TOKEN);
    if (!cookieToken) {
      localStorage.removeItem(LOCALSTORAGE_KEYS.TOKEN);
      localStorage.removeItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(LOCALSTORAGE_KEYS.ID_TOKEN);
      return {};
    }
    return { token, refreshToken, idToken };
  }

  const cookieToken = getCookie(SSO_COOKIE_KEYS.TOKEN);
  const cookieRefresh = getCookie(SSO_COOKIE_KEYS.REFRESH_TOKEN);
  const cookieId = getCookie(SSO_COOKIE_KEYS.ID_TOKEN);

  if (cookieToken && cookieRefresh) {
    localStorage.setItem(LOCALSTORAGE_KEYS.TOKEN, cookieToken);
    localStorage.setItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN, cookieRefresh);
    if (cookieId) localStorage.setItem(LOCALSTORAGE_KEYS.ID_TOKEN, cookieId);
    return { token: cookieToken, refreshToken: cookieRefresh, idToken: cookieId };
  }

  return {};
}

export function clearPersistedTokens(): void {
  localStorage.removeItem(LOCALSTORAGE_KEYS.TOKEN);
  localStorage.removeItem(LOCALSTORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(LOCALSTORAGE_KEYS.ID_TOKEN);
  deleteCookie(SSO_COOKIE_KEYS.TOKEN);
  deleteCookie(SSO_COOKIE_KEYS.REFRESH_TOKEN);
  deleteCookie(SSO_COOKIE_KEYS.ID_TOKEN);
}
